import api from './axios';

/** Contract: CITS-Server/docs/migration-runner-api.md */

export const REGISTRY_TARGET = '__registry__';

export type CellState = 'applied' | 'partial' | 'failed' | 'interrupted' | 'resolved' | 'running' | 'not_run' | 'no_db';

export interface RunnerSchema {
  batches: boolean;
  statementRuns: boolean;
  runsExtended: boolean;
  backupsExtended: boolean;
}

export interface GridTarget {
  targetDb: string;
  scope: 'tenant' | 'registry';
  name: string;
  code: string | null;
  companyId: string | null;
  exists: boolean | null;
  status?: string;
  databaseName?: string | null;
  sizeBytes?: number | null;
  tables?: number | null;
}

export interface GridMigration {
  scriptId: number;
  folderPath: string;
  folder: string;
  filename: string;
  scope: 'tenant' | 'registry';
  scopeInferred: boolean;
  version: number;
  archived: boolean;
  contentChangedSinceLast: boolean;
}

export interface GridCell {
  state: CellState;
  /** Raw run status (rolled_back / skipped / pending read as not_run). */
  status?: string;
  runRowId?: number | null;
  lastRun?: string;
  batchId?: string | null;
  errorKind?: string;
  errorReason?: string;
  errorCode?: string | number | null;
  failedIndex?: number;
  resolvedBy?: string;
  resolvedNote?: string;
}

export interface GridAlert {
  state: 'failed' | 'partial' | 'interrupted';
  migration: string;
  scriptId: number;
  targetDb: string;
  companyName: string;
  runRowId: number | null;
  failedIndex: number | null;
  reason: string | null;
  errorKind: string | null;
  lastRun: string | null;
}

export interface StatusGrid {
  runnerReady: boolean;
  schema: RunnerSchema;
  targets: GridTarget[];
  migrations: GridMigration[];
  cells: Record<number, Record<string, GridCell>>;
  alerts: GridAlert[];
  kpis: {
    companies: number;
    noDatabase: number;
    pendingMigrations: number;
    pendingCells: number;
    failed: number;
    partial: number;
    interrupted: number;
    folderErrors: number;
    folderWarnings: number;
  };
}

export interface RunnerSettings {
  stop_on_failure: boolean;
  backup: 'always' | 'ask';
  statement_timeout_s: number;
  lock_wait_s: number;
  block_non_idempotent: boolean;
  notify: 'failures' | 'all' | 'off';
  /** Folders dated before today minus N days count as archived; 0 = off. */
  archive_older_than_days?: number;
}

export interface S3Info {
  configured: boolean;
  bucket: string | null;
  prefix: string;
  endpoint: string | null;
}

export type StepStatus =
  | 'pending' | 'running' | 'success' | 'failed' | 'partial' | 'locked' | 'blocked'
  | 'skipped' | 'not_run' | 'cancelled' | 'interrupted';

export interface BatchStep {
  i: number;
  migrationId: number;
  migrationVersion: number;
  migrationName: string;
  scope: 'tenant' | 'registry';
  targetDb: string;
  companyId: string | null;
  companyName: string;
  fromIndex: number;
  status: StepStatus;
  reason: string | null;
  error: string | null;
  errorCode: string | null;
  sqlState: string | null;
  failedIndex: number | null;
  runRowId: number | null;
  statementCount: number | null;
  applied?: number;
  durationMs: number | null;
  backupId?: string | null;
  backupWarning?: string | null;
  /** Rollout step this step belongs to (batches with options.steps). */
  ring?: number;
  /** reason 'requires': the -- @requires folders not applied on this target. */
  requires?: { folder: string; reason: 'not_found' | 'not_applied'; files?: string[] }[];
  finishedAt?: string;
}

export type RingStatus = 'waiting' | 'running' | 'paused' | 'passed' | 'failed' | 'not_run';

export interface BatchRollout {
  pauseOnFailure: boolean;
  waitMinutes: number;
  paused: { ring: number; reason: 'failure' | 'wait'; at: string; resumeAfter: string | null } | null;
  releasedRing: number;
  events: { ring: number; reason: string; continuedBy: string; auto: boolean; at: string }[];
  rings: { index: number; label: string | null; targets: string[]; status: RingStatus; counts: Partial<Record<StepStatus, number>>; finishedAt: string | null }[];
}

export interface RolloutStep {
  label?: string | null;
  targets: string[];
}

export interface Batch {
  id: string;
  createdBy: string;
  status: 'queued' | 'running' | 'paused' | 'done' | 'failed' | 'cancelled' | 'interrupted';
  cancelRequested: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  options: {
    order?: 'migration_first' | 'tenant_first';
    stopOnFailure?: boolean;
    backup?: boolean;
    allowNonIdempotent?: boolean;
    label?: string | null;
    kind?: string;
    sourceRunRowId?: number | null;
    sourceBatchId?: string | null;
  };
  summary: { total: number; counts: Partial<Record<StepStatus, number>>; companies: number; migrations: number };
  rollout?: BatchRollout | null;
  steps?: BatchStep[];
}

export interface BatchOptions {
  order?: 'migration_first' | 'tenant_first';
  stopOnFailure?: boolean;
  backup?: boolean;
  allowNonIdempotent?: boolean;
  /** Only these `${migrationId}|${targetDb}` pairs, not every migration × database. */
  onlyPairs?: string[];
  /** Rollout in steps: run these groups one after another. */
  steps?: RolloutStep[];
  pauseOnFailure?: boolean;
  waitMinutes?: number;
}

// ── Phase 2: second approval, safety, drift ────────────────────────────────

export type ApprovalAction = 'restore_replace' | 'console_write' | 'company_data_delete' | 'batch_all_companies';
export type ApprovalStatus = 'pending' | 'approved' | 'declined' | 'expired' | 'used' | 'cancelled';

export interface ApprovalRequest {
  id: string;
  action: ApprovalAction;
  actionLabel: string;
  payload: Record<string, unknown>;
  summary: string | null;
  reason: string;
  requestedBy: string;
  status: ApprovalStatus;
  approvedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
}

/** The 428 body a guarded action answers with. */
export interface ApprovalRequired {
  code: 'APPROVAL_REQUIRED';
  message: string;
  action: ApprovalAction;
  payload: Record<string, unknown>;
  summary: string | null;
}

export interface OpsSafety {
  approvals: Record<ApprovalAction, 'always' | 'optional'>;
  maintenance_banner: boolean;
}

export interface MaintenanceRow {
  companyId: number;
  companyCode: string;
  companyName: string;
  reason: 'migration' | 'restore';
  sourceId: string | null;
  startedAt: string;
  expiresAt: string;
}

export interface SafetyInfo {
  settings: OpsSafety;
  schema: { approvals: boolean; maintenance: boolean; pausedStatus: boolean };
  actions: Record<ApprovalAction, string>;
  maintenance: MaintenanceRow[];
  otherOwners: { email: string; name: string | null }[];
  me: string;
}

export interface DriftItem {
  kind: 'table' | 'column' | 'index';
  table: string;
  name: string;
  sql: string;
}

export interface DriftFix {
  databaseName: string;
  companyId: number;
  companyName: string;
  generatedAt: string;
  items: DriftItem[];
  notes: string[];
  extra: { tables: string[]; columns: string[] };
  sql: string;
  sqlHash: string;
  suggestions: { folder: string; files: { scriptId: number; folderPath: string }[]; covers: string[] }[];
}

export interface NonIdempotentFinding {
  migrationId: number;
  migration: string;
  statements: { index: number; sql: string; message: string }[];
}

export interface RunDetail {
  run: {
    id: number;
    run_id: string;
    batch_id?: string | null;
    migration_id: number | null;
    migration_version: number | null;
    target_db: string;
    company_id: string | null;
    company_name: string | null;
    status: string;
    error_message: string | null;
    error_code?: string | null;
    sql_state?: string | null;
    failed_statement_index?: number | null;
    executed_by: string;
    executed_at: string;
    duration_ms: number | null;
    resolved_by?: string | null;
    resolved_note?: string | null;
    resolved_at?: string | null;
    resolution?: string | null;
    mark_id?: string | null;
    migration_name: string | null;
    folder_path: string | null;
  };
  sqlSnapshot: string;
  statements: {
    index: number;
    sql: string;
    status: 'applied' | 'failed' | 'skipped' | 'not_run' | 'unknown';
    errorCode: string | null;
    sqlState: string | null;
    errorMessage: string | null;
    durationMs: number | null;
  }[];
  statementHistory: boolean;
  backup: { id: string; sizeBytes: number | null; storage: string; verifiedAt: string | null; takenAt: string } | null;
  resumeFrom: number | null;
  unsafeForRerun: { index: number; message: string }[];
}

export interface Prediction {
  index: number;
  outcome: 'apply' | 'noop' | 'fail' | 'unknown';
  notes: string[];
  rows: number | null;
}

export interface PreviewResult {
  migration: { id: number; name: string; version: number; scope: 'tenant' | 'registry' } | null;
  statements: { index: number; sql: string; kind: string; summary: string; idempotent: boolean; issue: string | null }[];
  summary: { counts: Record<string, number>; hasDestructive: boolean };
  fileIssues: LintIssue[];
  blocked: boolean;
  previews: Record<string, { predictions?: Prediction[]; summary?: Record<string, number>; error?: string }>;
}

export interface LintIssue {
  severity: 'error' | 'warn';
  rule: string;
  path: string;
  message: string;
  statementIndex?: number;
  statement?: string;
}

export interface LintResult {
  issues: LintIssue[];
  files: number;
  errors: number;
  warnings: number;
}

export interface HistoryRun {
  id: number;
  run_id: string;
  batch_id?: string | null;
  migration_id: number | null;
  migration_version: number | null;
  target_db: string;
  company_name: string | null;
  status: string;
  error_message: string | null;
  error_code?: string | null;
  failed_statement_index?: number | null;
  executed_by: string;
  executed_at: string;
  duration_ms: number | null;
  migration_name: string | null;
  folder_path: string | null;
  resolved_by?: string | null;
  resolved_note?: string | null;
  resolution?: string | null;
  mark_id?: string | null;
}

export interface HealthCompany {
  companyId: string;
  companyName: string;
  companyCode: string;
  databaseName: string;
  state: 'in_sync' | 'drift' | 'unreachable' | 'no_db';
  missingTables?: string[];
  missingColumns?: string[];
  extraTables?: string[];
  extraColumns?: string[];
  suggestions?: { scriptId: number; folderPath: string; covers: string[] }[];
  /** Extras marked "It's fine, ignore" on Clean up (left out of extraTables/extraColumns). */
  ignored?: number;
}

export interface SchemaHealth {
  checkedAt: string;
  total: number;
  inSync: number;
  drift: number;
  unreachable: number;
  noDatabase: number;
  companies: HealthCompany[];
}

export interface BackupRow {
  id: string;
  targetDb: string;
  liveDatabase: string;
  scope: 'tenant' | 'registry';
  companyName: string | null;
  companyCode: string | null;
  backupType: string;
  status: 'in_progress' | 'completed' | 'failed' | 'expired';
  sizeBytes: number | null;
  storage: 'local' | 's3';
  verifiedAt: string | null;
  startedAt: string;
  completedAt: string | null;
  createdBy: string | null;
  error: string | null;
  fileName: string | null;
  lastRestore: {
    status: 'running' | 'done' | 'failed';
    mode: 'copy' | 'replace';
    by: string;
    startedAt: string;
    finishedAt?: string;
    database?: string;
    preRestoreBackupId?: string | null;
    error?: string;
  } | null;
}

export interface ConsoleResult {
  success: boolean;
  results: Record<string, unknown>[];
  rowCount?: number;
  affectedRows?: number;
  executionTime: number;
  queryType: string;
  backupId?: string;
}

// ── Clean up ───────────────────────────────────────────────────────────────

export type MarkOutcome = 'applied_by_hand' | 'not_needed' | 'found_in_place' | 'baseline';

export interface CleanupSchema {
  marks: boolean;
  scans: boolean;
  ignores: boolean;
  runColumns: boolean;
  ready: boolean;
}

export interface CleanupOverview {
  schema: CleanupSchema;
  runnerReady: boolean;
  counts: { pending: number; stuck: number; badge: number };
  pendingScan: { id: string; status: ScanStatus; finishedAt: string | null; progress: ScanProgress; summary: ScanSummary | null } | null;
  lint: { errors: number; warnings: number };
  /** Runner setting: folders older than this many days count as archived (0 = off). */
  archiveOlderThanDays: number;
}

export type StuckKind = 'running' | 'interrupted' | 'partial' | 'failed' | 'locked' | 'blocked' | 'skipped' | 'rolled_back' | 'pending';

export interface StuckItem {
  key: string;
  scriptId: number;
  folderPath: string;
  folder: string;
  scope: 'tenant' | 'registry';
  targetDb: string;
  companyName: string;
  kind: StuckKind;
  runRowId: number | null;
  lastRun: string | null;
  failedIndex: number | null;
  reason: string | null;
  errorKind?: string | null;
  batchId: string | null;
  requires?: { folder: string; reason: string }[] | null;
}

export interface MarkPair {
  scriptId?: number;
  targetDb?: string;
  runRowId?: number;
  outcome?: MarkOutcome;
  note?: string;
}

export interface MarkResult {
  markId: string | null;
  marked: number;
  inserted: number;
  updated: number;
  skipped: { scriptId?: number; targetDb?: string; runRowId?: number; reason: string }[];
}

export interface ClearResult {
  markId: string | null;
  listed: number;
  marked: number;
  skipped: MarkResult['skipped'];
}

export interface MarkRow {
  id: string;
  kind: 'mark' | 'bulk' | 'baseline' | 'stop' | 'clear_stuck' | 'clear_pending';
  outcome: string | null;
  note: string | null;
  count: number;
  summary: { archived?: string[]; cutoff?: string; counts?: Record<string, number> } | null;
  hidden: boolean;
  createdBy: string;
  createdAt: string;
  undoneAt: string | null;
  undoneBy: string | null;
  undoUntil: string | null;
  canUndo: boolean;
}

export type Verdict = 'inplace' | 'needed' | 'check' | 'obsolete' | 'unknown';
export type ScanStatus = 'running' | 'done' | 'failed';

export interface ScanProgress { done: number; total: number; current?: string | null }

export interface ScanItem {
  scriptId: number;
  folderPath: string;
  folder: string;
  scope: 'tenant' | 'registry';
  targetDb: string;
  companyName: string;
  state: CellState;
  runRowId: number | null;
  verdict: Verdict;
  detail: string;
  rows: number | null;
}

export interface ScanSummary {
  total: number;
  counts: Record<Verdict, number>;
  checkZero: number;
  byTarget: Record<string, { name: string } & Record<Verdict, number>>;
}

export interface Scan {
  id: string;
  kind: 'pending' | 'baseline';
  status: ScanStatus;
  cutoff: string | null;
  progress: ScanProgress;
  createdBy: string;
  createdAt: string;
  finishedAt: string | null;
  error: string | null;
  result?: {
    items: ScanItem[];
    dumps: (Omit<ScanItem, 'verdict' | 'detail' | 'rows'> & { rule: string })[];
    summary: ScanSummary;
    cutoff: string | null;
  } | null;
}

export interface BaselineResult {
  markId: string | null;
  marked: number;
  skipped: number;
  archivedFiles: number;
  archivedFolders: number;
  left: { needed: number; unknown: number; check: number };
}

export type TemplateCategory = 'template_behind' | 'company_behind' | 'leftover';

export interface TemplateGroup {
  id: string;
  category: TemplateCategory;
  kind: 'table' | 'column';
  table: string;
  columns: string[];
  items: string[];
  inTemplate: boolean;
  has: string[];
  lack: string[];
  sourceHas: boolean | null;
  templateStale: boolean;
  ignoredOn: string[];
  ignored: boolean;
  migrations: { scriptId: number; folderPath: string; folder: string; notAppliedOn: string[] }[];
}

export interface TemplateDiff {
  checkedAt: string;
  source: { databaseName: string; companyName: string | null; reachable: boolean } | null;
  companies: { databaseName: string; companyName: string; isSource: boolean; state: 'ok' | 'unreachable' | 'no_db' }[];
  groups: TemplateGroup[];
  counts: Record<TemplateCategory | 'ignored', number>;
}

export interface TemplatePlan {
  pairs: string[];
  migrationIds: number[];
  targets: string[];
  steps: { label: string | null; targets: string[]; migrations: string[] }[];
  driftOnly: { databaseName: string; items: string[] }[];
  planHash: string;
  dryRun: { apply: number; noop: number; fail: number; unknown: number; errors: number } | null;
  names: Record<string, string>;
  source: TemplateDiff['source'];
}

export interface LeftoverDraft {
  folder: string;
  filename: string;
  path: string;
  sql: string;
  notes: string[];
}

const P = '/super-admin/database/runner';

export const runnerApi = {
  status: (includeArchived = false) =>
    api.get<StatusGrid>(`${P}/status`, { params: includeArchived ? { includeArchived: 1 } : {} }).then((r) => r.data),
  settings: () => api.get<{ settings: RunnerSettings; schema: RunnerSchema; s3: S3Info }>(`${P}/settings`).then((r) => r.data),
  saveSettings: (settings: RunnerSettings) => api.put<{ settings: RunnerSettings }>(`${P}/settings`, { settings }).then((r) => r.data.settings),
  lint: () => api.get<LintResult>(`${P}/lint`).then((r) => r.data),
  preview: (body: { migrationId?: number; sql?: string; targets: string[] }) =>
    api.post<PreviewResult>(`${P}/preview`, body).then((r) => r.data),

  createBatch: (body: { migrationIds: number[]; targets: string[]; options?: BatchOptions; approvalId?: string }) =>
    api.post<{ batch: Batch }>(`${P}/batches`, body).then((r) => r.data.batch),
  batches: (params: Record<string, string | number | undefined> = {}) =>
    api.get<{ batches: Batch[] }>(`${P}/batches`, { params }).then((r) => r.data.batches),
  batch: (id: string) => api.get<{ batch: Batch }>(`${P}/batches/${id}`).then((r) => r.data.batch),
  cancelBatch: (id: string, mode: 'after_target' | 'now' = 'after_target') =>
    api.post<{ batch: Batch }>(`${P}/batches/${id}/cancel`, { mode }).then((r) => r.data.batch),
  retryBatch: (id: string, options: BatchOptions = {}) =>
    api.post<{ batch: Batch }>(`${P}/batches/${id}/retry`, { options }).then((r) => r.data.batch),
  continueBatch: (id: string) => api.post<{ batch: Batch }>(`${P}/batches/${id}/continue`).then((r) => r.data.batch),
  rolloutPlan: (targets: string[]) =>
    api.post<{ steps: RolloutStep[]; sizes?: Record<string, number | null>; available: boolean }>(`${P}/rollout/plan`, { targets }).then((r) => r.data),
  batchLog: (id: string) => api.get<string>(`${P}/batches/${id}/log`, { responseType: 'text' as const }).then((r) => r.data),

  run: (rowId: number) => api.get<RunDetail>(`${P}/runs/${rowId}`).then((r) => r.data),
  resume: (rowId: number, body: { fromIndex?: number; options?: BatchOptions } = {}) =>
    api.post<{ batch: Batch }>(`${P}/runs/${rowId}/resume`, body).then((r) => r.data.batch),
  rerun: (rowId: number, options: BatchOptions = {}) =>
    api.post<{ batch: Batch }>(`${P}/runs/${rowId}/rerun`, { options }).then((r) => r.data.batch),
  resolve: (rowId: number, note: string, outcome: 'applied_by_hand' | 'not_needed') =>
    api.post<{ id: number; status: string; markId?: string | null }>(`${P}/runs/${rowId}/resolve`, { note, outcome }).then((r) => r.data),

  history: (params: Record<string, string | number | undefined>) =>
    api.get<{ runs: HistoryRun[] }>(`${P}/history`, { params }).then((r) => r.data.runs),
  historyCsv: (params: Record<string, string | number | undefined>) =>
    api.get<Blob>(`${P}/history`, { params: { ...params, format: 'csv' }, responseType: 'blob' }).then((r) => r.data),

  schemaHealth: () => api.get<SchemaHealth>(`${P}/schema-health`).then((r) => r.data),

  backups: () => api.get<{ s3: S3Info; backups: BackupRow[] }>(`${P}/backups`).then((r) => r.data),
  backupNow: (target: string) => api.post<{ backupId: string }>(`${P}/backups`, { target }).then((r) => r.data),
  restore: (id: string, mode: 'copy' | 'replace', confirm?: string, approvalId?: string) =>
    api.post<{ backupId: string; mode: string; liveDatabase: string }>(`${P}/backups/${id}/restore`, { mode, confirm, approvalId }).then((r) => r.data),

  consoleRead: (query: string, target: string) => api.post<ConsoleResult>(`${P}/console/read`, { query, target }).then((r) => r.data),
  consoleWrite: (query: string, target: string, approvalId?: string) =>
    api.post<ConsoleResult>(`${P}/console/write`, { query, target, approvalId }).then((r) => r.data),

  safety: () => api.get<SafetyInfo>(`${P}/safety`).then((r) => r.data),
  saveSafety: (settings: Partial<OpsSafety>) => api.put<{ settings: OpsSafety }>(`${P}/safety`, { settings }).then((r) => r.data.settings),
  approvals: (status?: string) =>
    api.get<{ approvals: ApprovalRequest[]; me: string }>(`${P}/approvals`, { params: status ? { status } : {} }).then((r) => r.data),
  approval: (id: string) => api.get<{ approval: ApprovalRequest }>(`${P}/approvals/${id}`).then((r) => r.data.approval),
  askApproval: (body: { action: ApprovalAction; payload: Record<string, unknown>; reason: string; summary?: string | null }) =>
    api.post<{ approval: ApprovalRequest; otherOwners: { email: string; name: string | null }[] }>(`${P}/approvals`, body).then((r) => r.data),
  decideApproval: (id: string, decision: 'approve' | 'decline', note?: string) =>
    api.post<{ approval: ApprovalRequest }>(`${P}/approvals/${id}/decide`, { decision, note }).then((r) => r.data.approval),
  cancelApproval: (id: string) => api.post<{ approval: ApprovalRequest }>(`${P}/approvals/${id}/cancel`).then((r) => r.data.approval),

  drift: (targetDb: string) => api.get<DriftFix>(`${P}/drift/${encodeURIComponent(targetDb)}`).then((r) => r.data),
  driftFile: (targetDb: string) =>
    api.get<string>(`${P}/drift/${encodeURIComponent(targetDb)}`, { params: { download: 1 }, responseType: 'text' as const }).then((r) => r.data),
  runDrift: (targetDb: string, sqlHash: string) =>
    api.post<{ runId: string; rowId: number | null; items: number }>(`${P}/drift/${encodeURIComponent(targetDb)}/run`, { sqlHash }).then((r) => r.data),

  cleanupOverview: () => api.get<CleanupOverview>(`${P}/cleanup/overview`).then((r) => r.data),
  cleanupBootstrap: () =>
    api.post<{ results: { migration: string; status: string; error: string | null }[]; schema: CleanupSchema }>(`${P}/cleanup/bootstrap`).then((r) => r.data),
  stuck: () => api.get<{ items: StuckItem[] }>(`${P}/cleanup/stuck`).then((r) => r.data.items),
  clearStuck: (note?: string) =>
    api.post<ClearResult & { skippedRunning: { lockHeld: number; moving: number; unknown: number } }>(`${P}/cleanup/stuck/clear`, { note }).then((r) => r.data),
  clearPending: (cutoff: string, note?: string) =>
    api.post<ClearResult>(`${P}/cleanup/pending/clear`, { cutoff, note }).then((r) => r.data),
  mark: (items: MarkPair[], outcome: MarkOutcome, note: string) =>
    api.post<MarkResult>(`${P}/cleanup/marks`, { items, outcome, note }).then((r) => r.data),
  marks: (params: { limit?: number; includeHidden?: 0 | 1 } = {}) =>
    api.get<{ marks: MarkRow[] }>(`${P}/cleanup/marks`, { params }).then((r) => r.data.marks),
  undoMark: (id: string) =>
    api.post<{ deleted: number; restored: number; changed: number; unarchived: number }>(`${P}/cleanup/marks/${id}/undo`).then((r) => r.data),
  markStopped: (rowId: number) => api.post<{ markId: string; status: string }>(`${P}/cleanup/runs/${rowId}/stop`).then((r) => r.data),
  archiveFolders: (folders: string[], reason?: string) =>
    api.post<{ archived: number; paths: string[] }>(`${P}/cleanup/archive`, { folders, reason }).then((r) => r.data),
  unarchivePaths: (paths: string[]) => api.post<{ unarchived: number }>(`${P}/cleanup/unarchive`, { paths }).then((r) => r.data),
  startScan: (kind: 'pending' | 'baseline', cutoff = 'all') =>
    api.post<{ scan: Scan }>(`${P}/cleanup/scans`, { kind, cutoff }).then((r) => r.data.scan),
  scan: (id: string) => api.get<{ scan: Scan }>(`${P}/cleanup/scans/${id}`).then((r) => r.data.scan),
  latestScan: (kind: 'pending' | 'baseline', cutoff?: string) =>
    api.get<{ scan: Scan | null }>(`${P}/cleanup/scans/latest`, { params: { kind, cutoff } }).then((r) => r.data.scan),
  baseline: (body: { scanId: string; archiveDone: boolean; markDumps: boolean; hidden: boolean }) =>
    api.post<BaselineResult>(`${P}/cleanup/baseline`, body).then((r) => r.data),
  templateDiff: (showIgnored = false) =>
    api.get<TemplateDiff>(`${P}/cleanup/template`, { params: showIgnored ? { showIgnored: 1 } : {} }).then((r) => r.data),
  ignoreItems: (items: { databaseName: string; item: string }[], note?: string) =>
    api.post<{ ignored: number }>(`${P}/cleanup/template/ignore`, { items, note }).then((r) => r.data),
  unignoreItems: (items: { databaseName: string; item: string }[]) =>
    api.post<{ removed: number }>(`${P}/cleanup/template/unignore`, { items }).then((r) => r.data),
  draftLeftover: (items: { table: string; column?: string }[], fromDb: string) =>
    api.post<LeftoverDraft>(`${P}/cleanup/template/draft`, { items, fromDb }).then((r) => r.data),
  templatePlan: () => api.get<TemplatePlan>(`${P}/cleanup/template/plan`).then((r) => r.data),
  runTemplatePlan: (planHash: string) =>
    api.post<{ batch: Batch }>(`${P}/cleanup/template/plan/run`, { planHash }).then((r) => r.data.batch),

  bootstrap: () =>
    api.post<{ results: { migration: string; status: string; error: string | null; failedIndex: number | null }[]; schema: RunnerSchema }>(`${P}/bootstrap`).then((r) => r.data),
};

/** Server error → one readable line (code-aware). */
export function errorText(err: unknown, fallback = 'Something went wrong'): string {
  const e = err as { response?: { data?: { code?: string; message?: string } }; message?: string };
  const code = e?.response?.data?.code;
  if (code === 'reauth_required') return 'Sign in again first: this needs a fresh operator session.';
  if (code === 'two_factor_required') return 'Turn on two-step sign-in for your operator account first.';
  return e?.response?.data?.message || e?.message || fallback;
}

export function errorData<T = Record<string, unknown>>(err: unknown): (T & { code?: string; message?: string }) | null {
  const e = err as { response?: { data?: T & { code?: string; message?: string } } };
  return e?.response?.data ?? null;
}

export function downloadBlob(data: Blob | string, filename: string, type = 'text/plain') {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
